import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DailyEngagement, EngagementState } from './models/engagement.model';

@Component({
  selector: 'app-engagement-gauge',
  templateUrl: './engagement-gauge.component.html',
  styleUrl: './engagement-gauge.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EngagementGaugeComponent implements OnInit, OnChanges {
  @Input() engagementData: DailyEngagement[] = [];
  @Input() customerId: string;
  @Input() projectId: string;
  @Output() expandedChange = new EventEmitter<boolean>();

  dayLabels: string[] = [];
  readonly EngagementState = EngagementState;

  gaugeClasses: string[] = [];
  gaugeIcons: string[] = [];
  tooltips: string[] = [];

  latestIndex = -1;
  latestClass: string | undefined;
  latestIcon: string | undefined;
  latestTooltip: string | undefined;
  latestLabel: string | undefined;

  isExpanded = false;

  constructor(private cd: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.updateComputedData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['engagementData']) {
      this.updateComputedData();
    }
  }

  /**
   * Set labels for all 7 days
   */
  private setDayLabels(): void {
    this.dayLabels = [];

    // Oldest first: index 0 is 6 days ago, last index is today.
    for (let i = 6; i >= 0; i--) {
      const daysBack = i;
      const date = this.getUTCDateDaysBack(daysBack);

      if (daysBack === 0) {
        this.dayLabels.push('Today');
      } else {
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        this.dayLabels.push(dayName);
      }
    }
  }

  /**
   * Get UTC date N days back from today
   */
  private getUTCDateDaysBack(daysBack: number): Date {
    const today = new Date();
    const date = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - daysBack));
    return date;
  }

  /**
   * Create a map of dates to engagement data for quick lookup
   */
  private createEngagementMap(): Map<string, DailyEngagement> {
    const map = new Map<string, DailyEngagement>();
    for (const engagement of (this.engagementData || [])) {
      // Normalize date to YYYY-MM-DD format
      const dateStr = this.formatDateAsISO(engagement.date);
      map.set(dateStr, engagement);
    }
    return map;
  }

  /**
   * Format date as ISO string (YYYY-MM-DD)
   */
  private formatDateAsISO(dateInput: string | Date): string {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get engagement state for a given date, or UNKNOWN if no data
   */
  private getEngagementForDate(date: Date, engagementMap: Map<string, DailyEngagement>): DailyEngagement | null {
    const dateStr = this.formatDateAsISO(date);
    return engagementMap.get(dateStr) || null;
  }

  private updateComputedData(): void {
    this.setDayLabels();
    const engagementMap = this.createEngagementMap();

    this.gaugeClasses = [];
    this.gaugeIcons = [];
    this.tooltips = [];

    const baseClass = 'gauge-circle';

    // Oldest first: index 0 is 6 days ago, last index is today
    for (let i = 6; i >= 0; i--) {
      const daysBack = i;
      const date = this.getUTCDateDaysBack(daysBack);
      const engagement = this.getEngagementForDate(date, engagementMap);
      const state = engagement?.state ?? EngagementState.UNKNOWN;

      // Class
      this.gaugeClasses.push(`${baseClass} ${state}`);

      // Icon
      let icon = 'circle';
      switch (state) {
        case EngagementState.ENGAGED:
          icon = 'star';
          break;
        case EngagementState.ACTIVITY:
          icon = 'check';
          break;
        case EngagementState.LOGIN:
          icon = 'login';
          break;
      }
      this.gaugeIcons.push(icon);

      // Tooltip
      const dateStr = date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      let tooltip: string;
      if (!engagement) {
        tooltip = `${dateStr}: No data available`;
      } else {
        switch (engagement.state) {
          case EngagementState.ENGAGED:
            tooltip = `${dateStr}: Task activity + Comment activity`;
            break;
          case EngagementState.ACTIVITY:
            tooltip = `${dateStr}: Task activity (edit/add/complete)`;
            break;
          case EngagementState.LOGIN:
            tooltip = `${dateStr}: Login activity`;
            break;
          case EngagementState.NONE:
          default:
            tooltip = `${dateStr}: No engagement`;
            break;
        }
      }
      this.tooltips.push(tooltip);
    }

    // Latest item (today)
    this.latestIndex = this.gaugeClasses.length - 1;
    this.latestClass = this.gaugeClasses[this.latestIndex];
    this.latestIcon = this.gaugeIcons[this.latestIndex];
    this.latestTooltip = this.tooltips[this.latestIndex];
    this.latestLabel = this.dayLabels[this.latestIndex] ?? 'Today';
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    this.expandedChange.emit(this.isExpanded);
    this.cd.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }
}
